import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import type {
  MaterialsNodeData,
  ProposalNodeSet,
  SpeciesTimberConfig,
  StructureTimberListVersion,
  TimberListItem,
} from '../types';

function deepCloneTimberList(items: TimberListItem[]): TimberListItem[] {
  return JSON.parse(JSON.stringify(items)) as TimberListItem[];
}

function deepCloneAlternateSpecies(items: SpeciesTimberConfig[]): SpeciesTimberConfig[] {
  return JSON.parse(JSON.stringify(items)) as SpeciesTimberConfig[];
}

export function buildTimberSpeciesSummary(materials: MaterialsNodeData): string {
  const defaultSpecies =
    materials.timberList.find(i => i.species)?.species ||
    materials.alternateSpecies?.[0]?.speciesName ||
    'Default';
  const altCount = materials.alternateSpecies?.length || 0;
  if (!materials.quoteMultipleSpecies || altCount === 0) return defaultSpecies;
  return `${defaultSpecies} + ${altCount} alternate${altCount === 1 ? '' : 's'}`;
}

function fromFirestore(
  snap: { id: string; data: () => Record<string, unknown> },
): StructureTimberListVersion {
  const d = snap.data();
  return {
    id: snap.id,
    nodeSetId: String(d.nodeSetId || ''),
    nodeSetName: String(d.nodeSetName || 'Structure'),
    label: typeof d.label === 'string' ? d.label : undefined,
    createdAt: (d.createdAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
    createdBy: String(d.createdBy || ''),
    createdByName: typeof d.createdByName === 'string' ? d.createdByName : undefined,
    quoteMultipleSpecies: !!d.quoteMultipleSpecies,
    timberList: Array.isArray(d.timberList) ? deepCloneTimberList(d.timberList as TimberListItem[]) : [],
    alternateSpecies: Array.isArray(d.alternateSpecies)
      ? deepCloneAlternateSpecies(d.alternateSpecies as SpeciesTimberConfig[])
      : [],
    timberTotal: Number(d.timberTotal) || 0,
    speciesSummary: String(d.speciesSummary || ''),
  };
}

export async function createStructureTimberVersion(
  dealId: string,
  nodeSet: ProposalNodeSet,
  meta: { label?: string; createdBy: string; createdByName?: string },
): Promise<string> {
  const materials = nodeSet.materials;
  const payload = {
    nodeSetId: nodeSet.id,
    nodeSetName: (nodeSet.name || 'Structure').trim() || 'Structure',
    label: (meta.label || '').trim() || undefined,
    createdAt: serverTimestamp(),
    createdBy: meta.createdBy,
    createdByName: meta.createdByName,
    quoteMultipleSpecies: !!materials.quoteMultipleSpecies,
    timberList: deepCloneTimberList(materials.timberList || []),
    alternateSpecies: deepCloneAlternateSpecies(materials.alternateSpecies || []),
    timberTotal: Number(materials.timberTotal) || 0,
    speciesSummary: buildTimberSpeciesSummary(materials),
  };
  const ref = await addDoc(collection(db, 'proposalBuilders', dealId, 'timberVersions'), payload);
  return ref.id;
}

export async function listStructureTimberVersions(
  dealId: string,
  nodeSetId: string,
): Promise<StructureTimberListVersion[]> {
  const q = query(
    collection(db, 'proposalBuilders', dealId, 'timberVersions'),
    where('nodeSetId', '==', nodeSetId),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => fromFirestore({ id: d.id, data: () => d.data() }))
    .sort((a, b) => {
      const ad = a.createdAt instanceof Date ? a.createdAt.getTime() : a.createdAt?.toDate?.()?.getTime() ?? 0;
      const bd = b.createdAt instanceof Date ? b.createdAt.getTime() : b.createdAt?.toDate?.()?.getTime() ?? 0;
      return bd - ad;
    });
}

export async function getStructureTimberVersion(
  dealId: string,
  versionId: string,
): Promise<StructureTimberListVersion | null> {
  const versionSnap = await getDoc(doc(db, 'proposalBuilders', dealId, 'timberVersions', versionId));
  if (!versionSnap.exists()) return null;
  return fromFirestore({ id: versionSnap.id, data: () => versionSnap.data() as Record<string, unknown> });
}

export function materialsPatchFromTimberVersion(
  version: StructureTimberListVersion,
): Partial<MaterialsNodeData> {
  const timberList = deepCloneTimberList(version.timberList);
  const alternateSpecies = deepCloneAlternateSpecies(version.alternateSpecies || []);
  const totalBoardFeet = timberList.reduce((s, i) => s + (i.boardFeet || 0) * (i.quantity || 0), 0)
    + alternateSpecies.reduce(
        (s, sp) => s + (sp.timberList || []).reduce((ss, i) => ss + (i.boardFeet || 0) * (i.quantity || 0), 0),
        0,
      );
  return {
    quoteMultipleSpecies: version.quoteMultipleSpecies,
    timberList,
    alternateSpecies,
    timberTotal: version.timberTotal,
    totalBoardFeet,
  };
}

export function clearStructureTimberLists(): Partial<MaterialsNodeData> {
  return {
    timberList: [],
    alternateSpecies: [],
    quoteMultipleSpecies: false,
    timberTotal: 0,
    totalBoardFeet: 0,
  };
}

export function formatTimberVersionTimestamp(v: StructureTimberListVersion): string {
  const d = v.createdAt instanceof Date ? v.createdAt : v.createdAt?.toDate?.() ?? new Date();
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
